import React, { useState, useMemo } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
} from "lucide-react";
import { CampaignPost } from "../types/calendar";
import { SocialIcon } from "./SocialIcon";

interface CalendarViewProps {
  posts: CampaignPost[];
  onSelectPost: (post: CampaignPost) => void;
}

export const CalendarView: React.FC<CalendarViewProps> = ({
  posts,
  onSelectPost,
}) => {
  // Find initial month based on earliest post or current date
  const sortedDates = useMemo(() => {
    const valid = posts.filter((p) => p.rawDate).map((p) => p.rawDate as Date);
    return valid.sort((a, b) => a.getTime() - b.getTime());
  }, [posts]);

  const initialDate = sortedDates.length > 0 ? sortedDates[0] : new Date();

  const [currentYear, setCurrentYear] = useState(initialDate.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(initialDate.getMonth()); // 0-indexed

  // Month navigation
  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Map posts by YYYY-MM-DD string
  const postsByDateStr = useMemo(() => {
    const map: Record<string, CampaignPost[]> = {};
    posts.forEach((post) => {
      if (!post.date) return;
      const d = post.rawDate;
      if (d) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const key = `${yyyy}-${mm}-${dd}`;
        if (!map[key]) map[key] = [];
        map[key].push(post);
      }
    });
    return map;
  }, [posts]);

  // Generate calendar matrix (7 columns for Sun - Sat)
  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

    const startingDayOfWeek = firstDayOfMonth.getDay(); // 0 = Sun
    const totalDays = lastDayOfMonth.getDate();

    const days = [];
    // Padding from prev month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Month days
    for (let day = 1; day <= totalDays; day++) {
      const mm = String(currentMonth + 1).padStart(2, "0");
      const dd = String(day).padStart(2, "0");
      const dateKey = `${currentYear}-${mm}-${dd}`;
      days.push({
        dayNumber: day,
        dateKey,
        posts: postsByDateStr[dateKey] || [],
      });
    }
    return days;
  }, [currentYear, currentMonth, postsByDateStr]);

  const dayHeaders = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div
      style={{
        padding: "1.25rem 1.5rem",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        flex: 1,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Calendar Header Controls */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "var(--bg-surface)",
          padding: "0.85rem 1.25rem",
          borderRadius: "12px",
          border: "1px solid var(--border-color)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <CalendarIcon size={20} style={{ color: "var(--accent-primary)" }} />
          <h2
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "1.2rem",
              fontWeight: 700,
              color: "var(--text-main)",
            }}
          >
            {monthNames[currentMonth]} {currentYear}
          </h2>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <button
            onClick={prevMonth}
            style={{
              padding: "0.4rem 0.75rem",
              borderRadius: "8px",
              border: "1px solid var(--border-color)",
              background: "var(--bg-primary)",
              color: "var(--text-main)",
              display: "flex",
              alignItems: "center",
              gap: "0.25rem",
              fontSize: "0.85rem",
              fontWeight: 500,
            }}
          >
            <ChevronLeft size={16} /> Prev
          </button>
          <button
            onClick={() => {
              setCurrentMonth(initialDate.getMonth());
              setCurrentYear(initialDate.getFullYear());
            }}
            style={{
              padding: "0.4rem 0.75rem",
              borderRadius: "8px",
              border: "1px solid var(--border-color)",
              background: "var(--bg-primary)",
              color: "var(--text-main)",
              fontSize: "0.85rem",
              fontWeight: 500,
            }}
          >
            Current Month
          </button>
          <button
            onClick={nextMonth}
            style={{
              padding: "0.4rem 0.75rem",
              borderRadius: "8px",
              border: "1px solid var(--border-color)",
              background: "var(--bg-primary)",
              color: "var(--text-main)",
              display: "flex",
              alignItems: "center",
              gap: "0.25rem",
              fontSize: "0.85rem",
              fontWeight: 500,
            }}
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Unified 7-Column Grid Matrix with minmax(0, 1fr) for strictly equal column widths */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: "1px",
          background: "var(--border-color)",
          borderRadius: "12px",
          border: "1px solid var(--border-color)",
          overflow: "hidden",
          boxShadow: "var(--shadow-sm)",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Row 1: Weekday Headers (Sun through Sat) */}
        {dayHeaders.map((day) => (
          <div
            key={day}
            style={{
              background: "var(--bg-primary)",
              padding: "0.65rem 0.25rem",
              fontSize: "0.8rem",
              fontWeight: 700,
              color: "var(--text-muted)",
              textAlign: "center",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              borderBottom: "1px solid var(--border-color)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {day}
          </div>
        ))}

        {/* Rows 2+: Month Day Cells */}
        {calendarDays.map((cell, idx) => {
          if (!cell) {
            return (
              <div
                key={`empty-${idx}`}
                style={{
                  background: "var(--bg-primary)",
                  opacity: 0.4,
                  minHeight: "130px",
                  minWidth: 0,
                }}
              />
            );
          }

          const hasPosts = cell.posts.length > 0;

          return (
            <div
              key={cell.dateKey}
              style={{
                background: "var(--bg-surface)",
                padding: "0.5rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.35rem",
                minHeight: "130px",
                minWidth: 0,
                boxSizing: "border-box",
                transition: "background 0.15s ease",
              }}
            >
              {/* Day Number Header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingBottom: "0.15rem",
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    color: hasPosts ? "var(--text-main)" : "var(--text-light)",
                    width: "22px",
                    height: "22px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: hasPosts ? "var(--accent-glow)" : "transparent",
                    flexShrink: 0,
                  }}
                >
                  {cell.dayNumber}
                </span>

                {hasPosts && (
                  <span
                    style={{
                      fontSize: "0.68rem",
                      fontWeight: 600,
                      color: "var(--text-muted)",
                      flexShrink: 0,
                    }}
                  >
                    {cell.posts.length} post{cell.posts.length > 1 ? "s" : ""}
                  </span>
                )}
              </div>

              {/* Posts Cards list inside cell */}
              <div
                className="thin-scrollbar"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.3rem",
                  overflowY: "auto",
                  maxHeight: "160px",
                  minWidth: 0,
                }}
              >
                {cell.posts.map((post) => (
                  <div
                    key={post.postId}
                    onClick={() => onSelectPost(post)}
                    style={{
                      padding: "0.35rem 0.45rem",
                      borderRadius: "6px",
                      background: "var(--bg-primary)",
                      border: "1px solid var(--border-color)",
                      cursor: "pointer",
                      fontSize: "0.73rem",
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.2rem",
                      minWidth: 0,
                      transition:
                        "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = "var(--shadow-sm)";
                      e.currentTarget.style.borderColor =
                        "var(--accent-primary)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                      e.currentTarget.style.borderColor = "var(--border-color)";
                    }}
                  >
                    {/* Platform Icon & Post ID Row */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "0.2rem",
                        minWidth: 0,
                      }}
                    >
                      <SocialIcon platform={post.platform} size={14} />
                      <span
                        style={{
                          fontSize: "0.63rem",
                          color: "var(--text-light)",
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        {post.postId}
                      </span>
                    </div>

                    {/* Hook / Content Title */}
                    <div
                      style={{
                        fontWeight: 600,
                        color: "var(--text-main)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                      }}
                      title={post.hook || post.contentPillar || "Campaign Post"}
                    >
                      {post.hook || post.contentPillar || "Campaign Post"}
                    </div>

                    {/* Content Format */}
                    {post.format && (
                      <div
                        style={{
                          fontSize: "0.63rem",
                          color: "var(--text-muted)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {post.format}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
